import {
  addUser,
  checkUserExist,
  getEmployeeByName,
  updateUser
} from '@api/roleManagement';
import ZeissButton from '@components/button';
import ZeissInput from '@components/input';
import ZeissSelect from '@components/select';
import { roleList } from '@store';
import { Col, Form, Modal, Row, Space, message } from 'antd';
import _ from 'lodash';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useImmer } from 'use-immer';
import { ROLEIDLIST } from '../hooks/RoleManagement/type';
import './admin.scss';
import { useSectorMark } from '@hooks/useSectorMarkHook';

interface RecordData {
  userName?: string;
  phone?: string | number;
  email?: string;
  sector?: string;
  realName?: string;
  userId?: string;
  userTypesId?: string;
}
interface Props {
  open: boolean;
  setOpen: (val: boolean) => void;
  currentTabKey?: string;
  record?: RecordData;
  getDataSource?: () => void;
  dicList?: any;
}

interface SectorList {
  dictKey: string | any;
  dictValue: string;
  dictType: string;
}

interface UserList {
  employeeId: string;
  userName: string;
  mobile?: string;
  email?: string;
  sector?: {
    value: string;
    key?: number;
  }[];
  dictName: string;
}

export const AdminPopup = ({
  open,
  setOpen,
  currentTabKey,
  record,
  dicList,
  getDataSource
}: Props) => {
  const [form] = Form.useForm();
  const userName = Form.useWatch('userName', form);
  const [userList, setUserList] = useImmer([]);
  const [roleIdList] = useRecoilState<ROLEIDLIST[] | any>(roleList);
  const handleCancel = () => {
    setOpen(false);
    form.resetFields();
    setUserList([]);
  };
  const sectorMark = useSectorMark();

  const handleConfirm = async (val: UserList) => {
    const sector = dicList
      .filter(
        (x: SectorList) =>
          x.dictType === 'sector' && val.sector?.includes(x.dictKey)
      )
      .map((y: SectorList) => ({ key: y.dictKey, value: y.dictValue }));
    const userInfo: UserList[] = userList.filter(
      (x: UserList) => x.employeeId === val.userName
    );
    try {
      if (userInfo.length && !record?.userId) {
        // Sector 有Sector 选择，Admin 没有
        await addUser({
          ...val,
          userRole:
            roleIdList && currentTabKey
              ? [{ ...roleIdList[currentTabKey] }]
              : undefined,
          name: userInfo[0].userName,
          sector: currentTabKey === 'Sector' ? sector : undefined
        });
        message.success('新增成功');
        setOpen(false);
      } else {
        await updateUser({
          ...val,
          userRole:
            roleIdList && currentTabKey
              ? [{ ...roleIdList[currentTabKey] }]
              : undefined,
          name: userInfo.length ? userInfo[0].userName : val.userName,
          userId: record?.userId,
          sector: currentTabKey === 'Sector' ? sector : undefined
        });
        message.success('修改成功');
        setOpen(false);
      }
      getDataSource && getDataSource();
    } catch (error) {
      console.log(error);
    }
  };

  const onValuesChange = (val: RecordData) => {
    if (val.hasOwnProperty('userName')) {
      if (val.userName) {
        const userInfo: UserList[] = userList.filter(
          (x: UserList) => x.employeeId === val.userName
        );
        if (userInfo.length) {
          form.setFieldsValue({
            mobile: userInfo[0]?.mobile,
            email: userInfo[0]?.email
          });
        }
      } else {
        form.resetFields();
      }
    }
  };

  const setEmployeeByName = async (name: string) => {
    const res = await getEmployeeByName({
      name,
      pageNumber: 1,
      pageSize: 10
    });
    setUserList(res.records || []);
  };

  const onSearch = (value: string) => {
    if (value !== '') {
      setEmployeeByName(value);
    }
  };

  useEffect(() => {
    if (record?.userId) {
      form.setFieldsValue({
        ...record,
        userName: record.realName,
        sector: record.userTypesId ? record.userTypesId.split(',') : undefined
      });
    } else {
      form.resetFields();
    }
  }, [record]);

  return (
    <Modal
      open={open}
      width={520}
      centered
      footer={null}
      maskClosable={false}
      destroyOnClose={true}
      onCancel={handleCancel}
    >
      <div className="pc-createAnnouncementLayout">
        <div className="pc-createAnnouncement-title">
          {record?.userId ? '修改' : `新增${currentTabKey}`}
        </div>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleConfirm}
          onValuesChange={onValuesChange}
        >
          <Row gutter={[24, 0]} justify={'space-between'}>
            <Col span={24}>
              <Form.Item
                label={'姓名'}
                name={'userName'}
                rules={[
                  {
                    required: true,
                    validator: async (_, value) => {
                      if (value) {
                        const userInfo: UserList[] = userList.filter(
                          (x: UserList) => x.employeeId === value
                        );
                        if (userInfo.length) {
                          const res = await checkUserExist(
                            // userInfo[0].userName
                            value
                          );
                          if (!res) {
                            return Promise.resolve();
                          } else {
                            return Promise.reject(new Error('请勿重复添加'));
                          }
                        }
                        return Promise.resolve();
                      } else {
                        return Promise.reject(new Error('请选择姓名'));
                      }
                    }
                  }
                ]}
              >
                <ZeissSelect
                  allowClear
                  disabled={record?.userId ? true : false}
                  onSearch={_.debounce(onSearch, 600)}
                  placeholder={`请选择姓名`}
                  options={userList.map((x: UserList) => ({
                    label: x.userName,
                    value: x.employeeId
                  }))}
                />
              </Form.Item>
            </Col>
            {currentTabKey === 'Sector' && (
              <Col span={24}>
                <Form.Item
                  label={sectorMark}
                  name={'sector'}
                  rules={[{ required: true, message: `请选择${sectorMark}` }]}
                >
                  <ZeissSelect
                    mode="multiple"
                    allowClear
                    placeholder={`请选择${sectorMark}`}
                    options={dicList
                      ?.filter(
                        (y: { dictType: string }) => y.dictType === 'sector'
                      )
                      .map((x: SectorList) => ({
                        label: x.dictValue,
                        value: x.dictKey
                      }))}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24}>
              <Form.Item
                label={'手机号码'}
                name={'mobile'}
                rules={[
                  { required: true, message: '请选择姓名,选择姓名后自动带入' }
                ]}
              >
                <ZeissInput readOnly={true} placeholder={`输入姓名自动带入`} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={'邮箱'}
                name={'email'}
                rules={[
                  { required: true, message: '请选择姓名,选择姓名后自动带入' }
                ]}
              >
                <ZeissInput readOnly={true} placeholder={`输入姓名自动带入`} />
              </Form.Item>
            </Col>
          </Row>
          <Space className="pc-createAnn-footer" size="middle">
            <ZeissButton className="pc-normal-btn" onClick={handleCancel}>
              取消
            </ZeissButton>
            <ZeissButton
              className={`pc-confirm-btn ${
                userName ? '' : 'pc-confirmBtn-disable'
              }`}
              htmlType="submit"
            >
              保存
            </ZeissButton>
          </Space>
        </Form>
      </div>
    </Modal>
  );
};
